import type { Middleware } from '@reduxjs/toolkit'
import type { IReactNativeWebView } from '@amzn/ihm-associate-tools-js-lib'
import { setSelectedStoreId, setStoreId } from '@atredux/slices/storeSlice'
import { getEffectiveStoreId } from '@atredux/selectors/storeSelector'
import { setActiveStoreId } from '@services/StoreRegionRegistry'
import { rememberRegion } from '@services/RegionPreferenceCookie'
import { parseAppHost } from '@utils/AppHostUtil'
import { getRealmForStore } from '@utils/RegionRealms'
import type { RootState } from '@atredux/store'

/** The React Native WebView bridge, typed by @amzn/ihm-associate-tools-js-lib's `Window` declaration. */
function reactNativeWebView (): IReactNativeWebView | undefined {
    return window.ReactNativeWebView
}

/**
 * Middleware that keeps everything outside Redux in step with the active store.
 *
 * Three consumers:
 *  - React Native, which is told the store so tool logs and metrics are attributed to it, and the
 *    store's realm so it uploads them to the cell that owns the store rather than the cell the user
 *    authenticated in. Only SMP knows which cell a store is in, so the shell has to be told.
 *  - StoreRegionRegistry, which cannot read Redux itself (see its header) so the value is pushed here.
 *  - RegionPreferenceCookie: on setSelectedStoreId, refreshes the sign-in host's region (not the
 *    store's; Cognito pools are per region). Scanning a store (setStoreId) never touches it.
 *
 * Both the scanned and the merely-selected store matter, and the same precedence as
 * getEffectiveStoreId applies, so the effective id is recomputed from state after the reducer runs
 * rather than read off the action.
 */
export const storeContextMiddleware: Middleware = (api) => (next) => (action) => {
    const result = next(action)

    if (setSelectedStoreId.match(action) || setStoreId.match(action)) {
        const storeId = getEffectiveStoreId(api.getState() as RootState)
        setActiveStoreId(storeId)

        if (setSelectedStoreId.match(action)) {
            const selectedStoreId = typeof action.payload === 'string' && action.payload ? action.payload : null
            const nativeBridge = reactNativeWebView()
            if (selectedStoreId && nativeBridge) {
                // The realm is omitted rather than guessed when the store's cell is unknown, leaving the
                // shell uploading to the login region as it did before.
                const realm = getRealmForStore(selectedStoreId)
                nativeBridge.postMessage(
                    JSON.stringify({ type: 'store_context', storeId: selectedStoreId, ...(realm ? { region: realm } : {}) }),
                )
            }
            if (selectedStoreId) {
                const appHost = parseAppHost(window.location.hostname)
                if (appHost) rememberRegion(appHost.airport)
            }
        }
    }

    return result
}
